import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { RuleInterface } from '@vendasta/automata';
import { DurationPeriod, LineItemAppKey } from '@vendasta/sales-opportunities';
import { LineItem } from '@vendasta/sales-orders';
import { SalesOrderConfirmationComponent, SalesOrderConfirmationModule } from '@vendasta/sales-ui';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { startWith, take, tap } from 'rxjs/operators';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { AutomataI18nModule, AUTOMATION_MARKET_ID_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import { PipelineSelectorComponent } from '../../../common/shared-form-elements/pipeline-selector/pipeline-selector.component';
import { nonNegativeIntegerValidator, positiveIntegerValidator } from '../../../common/utils/number-validator';
import { ASSIGNED_SALESPERSON } from '../../../../../data-services/users.service';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { GalaxyWrapModule } from '@vendasta/galaxy/galaxy-wrap';
import { SalespersonSelectorComponent } from '../../../common/salesperson-selector/salesperson-selector.component';

@Component({
  selector: 'automata-create-opportunity-panel',
  templateUrl: './create-opportunity-panel.component.html',
  styleUrls: ['./create-opportunity-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    AutomataI18nModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    SalesOrderConfirmationModule,
    GalaxyWrapModule,
    PipelineSelectorComponent,
    SalespersonSelectorComponent,
  ],
})
export class CreateOpportunityPanelComponent
  extends CommonActionPanelComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly destroyed$$: Subject<void> = new Subject<void>();
  loading$$ = new BehaviorSubject(true);
  loading$ = this.loading$$.asObservable();

  @ViewChild('salesOrderConfirmation', { static: false }) salesOrderConfirmation: SalesOrderConfirmationComponent;
  lineItems$$ = new ReplaySubject<LineItem[]>(1);
  lineItems$ = this.lineItems$$.asObservable();

  lineItemsInvalid$$ = new BehaviorSubject<boolean>(false);
  lineItemsInvalid$ = this.lineItemsInvalid$$.asObservable();

  isNonPartnerAutomation$: Observable<boolean>;

  opportunityNameControl = new FormControl<string>('');
  assigneeFormControl = new FormControl<string>(ASSIGNED_SALESPERSON);

  @ViewChild('pipelineSelector', { static: false }) pipelineSelector: PipelineSelectorComponent;
  initialPipelineId: string;
  initialStageId: number;
  pipelineSelectorLoaded$$ = new Subject();

  expectedCloseNumDaysControl = new FormControl<string>(null);

  durationValueControl = new FormControl<number>(null);
  durationTimeFrameControl = new FormControl<string>('');
  durationOptions = [
    {
      label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_OPPORTUNITY.DURATION_PERIOD_OPTIONS.DAYS',
      value: DurationPeriod.DAY,
    },
    {
      label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_OPPORTUNITY.DURATION_PERIOD_OPTIONS.WEEKS',
      value: DurationPeriod.WEEK,
    },
    {
      label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_OPPORTUNITY.DURATION_PERIOD_OPTIONS.MONTHS',
      value: DurationPeriod.MONTH,
    },
    {
      label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_OPPORTUNITY.DURATION_PERIOD_OPTIONS.YEARS',
      value: DurationPeriod.YEAR,
    },
  ];

  opportunityTypeFormControl = new FormControl<string>('');
  opportunityTypeOptions = [
    {
      value: '',
      label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_OPPORTUNITY.NOT_SPECIFIED',
    },
    {
      value: 'new-business',
      label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_OPPORTUNITY.NEW_BUSINESS',
    },
    {
      value: 'existing-business',
      label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_OPPORTUNITY.EXISTING_BUSINESS',
    },
  ];

  form: UntypedFormGroup;
  hidePackages: boolean;
  marketId: string;

  constructor(
    protected readonly automationService: AutomationViewService,
    protected readonly cdr: ChangeDetectorRef,
    @Inject(AUTOMATION_MARKET_ID_INJECTION_TOKEN$)
    protected readonly marketId$: Observable<string>,
  ) {
    super();
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.isNonPartnerAutomation$ = this.automationService.isNonPartnerAutomation().pipe(
      take(1),
      tap((isNonPartnerAutomation) => {
        if (isNonPartnerAutomation) {
          // the pipeline selector is disabled for non-partner automations (templates), so complete its loading state
          this.pipelineSelectorLoaded();
          // have to specify the validators to glxy by using numberValidators input
          this.expectedCloseNumDaysControl.setValidators(nonNegativeIntegerValidator);
          this.durationValueControl.setValidators(Validators.min(0));
        } else {
          this.opportunityNameControl.setValidators(Validators.required);
          this.assigneeFormControl.setValidators(Validators.required);
          this.expectedCloseNumDaysControl.setValidators([Validators.required, nonNegativeIntegerValidator]);
          this.durationValueControl.setValidators(positiveIntegerValidator);
        }
      }),
    );

    // updates the market id for the sales order confirmation module's line items
    if (this.automation.marketId) {
      this.hidePackages = false;
      this.marketId = this.automation.marketId;
    } else {
      this.marketId$
        .pipe(
          take(1),
          tap((marketId) => {
            this.marketId = marketId;
          }),
        )
        .subscribe();
    }

    const jsonData = this.step ? JSON.parse(this.step.data) : '';
    if (jsonData) {
      this.opportunityNameControl.setValue(jsonData.opportunity_name);
      this.assigneeFormControl.setValue(jsonData.assigned_salesperson_id);
      this.opportunityTypeFormControl.setValue(jsonData.opportunity_type || '');
      this.expectedCloseNumDaysControl.setValue(jsonData.expected_close_date_num_days);
      this.durationValueControl.setValue(jsonData.expected_contract_duration?.value);
      this.durationTimeFrameControl.setValue(jsonData.expected_contract_duration?.duration);
      this.initialPipelineId = jsonData.pipeline_id;
      this.initialStageId = jsonData.probability;
      this.convertLineItems(jsonData.line_items || []);
    }

    this.form = new FormGroup({
      opportunityNameControl: this.opportunityNameControl,
      assigneeFormControl: this.assigneeFormControl,
      expectedCloseNumDaysControl: this.expectedCloseNumDaysControl,
      durationValueControl: this.durationValueControl,
      durationTimeFrameControl: this.durationTimeFrameControl,
      opportunityTypeFormControl: this.opportunityTypeFormControl,
    });

    this.form.valueChanges.pipe(startWith(null)).subscribe(() => {
      this.savingEnabled = !this.form.invalid && this.isFormValid();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    const controls = [];
    if (this.pipelineSelector) {
      controls.push(...this.pipelineSelector.getControlsToValidateOnSave());
    }
    controls.push(this.form);
    return controls;
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};

    if (!this.form) {
      return jsonData;
    }

    // the following are available to be set on templates or partner automations:
    jsonData.opportunity_name = this.opportunityNameControl.value;
    jsonData.assigned_salesperson_id = this.assigneeFormControl.value;
    jsonData.opportunity_type = this.opportunityTypeFormControl.value;
    jsonData.expected_close_date_num_days = this.expectedCloseNumDaysControl.value;
    if (this.durationValueControl.value) {
      jsonData.expected_contract_duration = {
        value: +this.durationValueControl.value,
        duration: this.durationTimeFrameControl.value,
      };
    }
    // end

    if (this.pipelineSelector) {
      const selectedPipeline = this.pipelineSelector.getSelectedPipeline();
      jsonData.pipeline_id = selectedPipeline.pipelineId;
      jsonData.probability = selectedPipeline.stageId;
    }

    if (this.salesOrderConfirmation) {
      const lineItems = this.salesOrderConfirmation.getCurrentLineItems();
      jsonData.line_items = lineItems.map((lineItem) => {
        const identifier = lineItem.packageId
          ? { package_id: lineItem.packageId }
          : { app_key: { app_id: lineItem.appKey.appId, edition_id: lineItem.appKey.editionId } };
        return {
          identifier,
          quantity: lineItem.quantity,
          is_trial: lineItem.isTrial,
          currency_code: lineItem.currencyCode,
          current_revenue: {
            revenue_components: lineItem.currentRevenue.revenueComponents.map((price) => ({
              value: price.value,
              period: price.period,
              is_starting_revenue: !!price.isStartingRevenue,
            })),
          },
        };
      });
    }
    return jsonData;
  }

  // convert the line item objects from underscores to camel case
  convertLineItems(lineItems: any): void {
    const itemsFromJSON = lineItems.map((lineItem) => {
      const item = {
        quantity: lineItem.quantity,
        isTrial: lineItem.is_trial,
        currencyCode: lineItem.currency_code,
        currentRevenue: {
          revenueComponents: lineItem.current_revenue.revenue_components.map((price) => ({
            value: price.value,
            period: price.period,
            isStartingRevenue: !!price.is_starting_revenue,
          })),
        },
      } as LineItem;
      if (lineItem.identifier.package_id) {
        item.packageId = lineItem.identifier.package_id;
      } else {
        item.appKey = {
          appId: lineItem.identifier.app_key.app_id,
          editionId: lineItem.identifier.app_key.edition_id,
        } as LineItemAppKey;
      }
      return item;
    });

    this.lineItems$$.next(itemsFromJSON);
  }

  getRules(): RuleInterface[] {
    return [];
  }

  isFormValid(): boolean {
    let pipelineSelectorValid = true;
    let salesOrderLineItems = true;
    if (this.pipelineSelector) {
      pipelineSelectorValid = this.pipelineSelector.isFormValid();
    }
    if (this.salesOrderConfirmation) {
      salesOrderLineItems = this.salesOrderConfirmation.getCurrentLineItems().length > 0;
    }

    this.lineItemsInvalid$$.next(!salesOrderLineItems);
    return salesOrderLineItems && pipelineSelectorValid && !this.form.invalid;
  }

  pipelineSelectorLoaded(): void {
    this.pipelineSelectorLoaded$$.next(true);
  }

  updateLoading(isLoading: boolean): void {
    this.loading$$.next(isLoading);
  }
}
